/**
 * Search result columns — derives the Lens result table from analyzer configuration (plan S4).
 *
 * THIS IS THE POINT OF S4. The result table is not a hardcoded column list: a tenant marks fields
 * `searchable` in their analyzer config and those fields become columns, in `displayOrder`, headed
 * by `displayName` and formatted by `dataType`. Changing the config changes the table with no code
 * change and no deploy. S8 adds a UI for editing the flags; the capability is here.
 *
 * WHY COLUMNS ARE PER DOCUMENT TYPE. Two analyzers have different fields, so a result set spanning
 * types has no common configured column. When the caller has narrowed to one document type, that
 * type's configured columns are shown; otherwise the shared metadata columns are, because a column
 * that can only be filled for some rows would render blank for the rest — which reads as missing
 * data rather than as an inapplicable column.
 */

import { AnalyzerConfiguration, FieldConfiguration, FieldDataType } from '../services/analyzerService';
import { DocumentStatus } from '../components/shared/StatusBadge';
import { applyFormat } from './normalizePreview';

/** One tenant-configured result column. */
export interface SearchFieldColumn {
  /** Extracted field name — the key sent as `include` and returned in `SearchHit.fields`. */
  fieldName: string;
  /** Column header. */
  label: string;
  dataType: FieldDataType;
  /** Display/format hint (e.g. "MM/DD/YYYY"), same one HITL preview and export already honor. */
  format?: string;
  /** Whether the cell clips to the column width with an ellipsis (R9). Default true. */
  truncate: boolean;
}

/**
 * The configured result columns for one document type, in `displayOrder`.
 *
 * A field is a column when `showInResults` is set, FALLING BACK to `searchable` when it is absent
 * (R9) — so a config written before the column/searchable split keeps rendering exactly as it did,
 * and a field can now be made searchable WITHOUT becoming a column by setting `showInResults:false`.
 *
 * Returns an empty list when no field qualifies — the caller then shows metadata columns only. An
 * empty list is a real statement about the config, not a failure.
 */
/**
 * Is this field a default result column? `showInResults` when set, else `searchable` (R9).
 *
 * Exported because the Lens settings screen has to answer the SAME question to seed its
 * "Show as column" checkbox. Two copies of this rule is two places to change when it moves, and a
 * drift between them shows up as the settings tab disagreeing with the results table about which
 * fields are columns — silently, and only for configs written before R9.
 */
export function isResultColumn(field: FieldConfiguration): boolean {
  return field.showInResults ?? field.searchable === true;
}

/** Does this column clip to width? Default ON, so a long-text column cannot bloat rows (R9). */
export function columnTruncates(field: FieldConfiguration): boolean {
  return field.truncateColumn ?? true;
}

export function buildSearchColumns(
  configs: AnalyzerConfiguration[],
  documentType: string | undefined
): SearchFieldColumn[] {
  if (!documentType) return [];

  const config = configs.find((candidate) => candidate.documentType === documentType);
  if (!config) return [];

  return config.fields
    .filter(isResultColumn)
    .slice()
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((field) => ({
      fieldName: field.fieldName,
      label: field.displayName || field.fieldName,
      dataType: field.dataType ?? 'string',
      format: field.format,
      // `truncate` here is DataTable's column flag; `truncateColumn` is the persisted config name.
      truncate: columnTruncates(field),
    }));
}

/**
 * Hoisted formatters. `toLocaleString(undefined, {...})` constructs a fresh `Intl.NumberFormat` on
 * every call — passing an options object defeats the engine's cached fast path — and this runs once
 * per cell, over a result set that grows without bound as pages are loaded.
 */
const CURRENCY_FORMAT = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' });
const NUMBER_FORMAT = new Intl.NumberFormat();
const DATE_FORMAT = new Intl.DateTimeFormat();

/**
 * Format an extracted value for display by its configured `dataType`.
 *
 * Returns an empty string for a missing value so the cell renders empty. It deliberately does NOT
 * substitute a dash or "N/A": the document genuinely does not carry that field, and a stand-in
 * glyph is a value the tenant never recorded.
 *
 * An unparseable date or number falls back to the raw string rather than being dropped — the value
 * IS what the document holds, and hiding it because it did not parse would be the search screen
 * silently disagreeing with the record.
 *
 * When the tenant configured a `format` hint on the field, it takes over formatting entirely —
 * the same hint HITL preview and export already honor via `applyFormat` (Lens S4 follow-up #3). A
 * tenant setting `MM/DD/YYYY` sees it in search too, not just HITL and export. Falls back to the
 * `dataType` locale default when no hint is configured.
 */
export function formatFieldValue(value: string | undefined, dataType: FieldDataType, format?: string): string {
  if (value === undefined || value === '') return '';

  if (format) return applyFormat(value, format);

  switch (dataType) {
    case 'date': {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? value : DATE_FORMAT.format(parsed);
    }
    case 'number': {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : NUMBER_FORMAT.format(parsed);
    }
    case 'currency': {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : CURRENCY_FORMAT.format(parsed);
    }
    default:
      return value;
  }
}

/**
 * Map a backend `currentStatus` onto the `StatusBadge` vocabulary.
 *
 * Search shows documents in every state, including the terminal ones the review queue never
 * displays, so this is deliberately not `HomePage`'s `mapQueueStatus` — that one resolves lock and
 * hold state for a queue row and has no case for `COMPLETED` or `ARCHIVED`.
 */
export function mapSearchStatus(status: string): DocumentStatus | undefined {
  switch (status) {
    case 'UPLOADED':
    case 'PROCESSING':
    case 'AI_EXTRACTION_COMPLETE':
    case 'OWNERSHIP_INFERENCE_COMPLETE':
      return 'processing';
    case 'PENDING_VALIDATION':
      return 'ready';
    case 'VALIDATION_IN_PROGRESS':
      return 'reviewing';
    case 'ON_HOLD':
      return 'on_hold';
    case 'FAILED':
      return 'error';
    case 'VALIDATED':
    case 'SUBMISSION_PENDING':
    case 'SUBMITTED':
    case 'COMPLETED':
    case 'AUTO_APPROVED':
    case 'PROCESSED':
    case 'ARCHIVED':
      return 'completed';
    default:
      // Deliberately NOT 'pending'. A badge reading "Pending — Awaiting processing" for a status
      // this map does not know is a confident false claim about the document's state, which in a
      // records system is worse than admitting the gap. The caller renders the raw status instead.
      return undefined;
  }
}

/**
 * Statuses offered in the filter, as `{ value, label }`.
 *
 * A curated subset of the transition map rather than all sixteen: the intermediate machine states
 * (`AI_EXTRACTION_COMPLETE`, `OWNERSHIP_INFERENCE_COMPLETE`) are moments in a pipeline that lasts
 * seconds, and offering them as filters implies a clerk might usefully search for one.
 */
export const SEARCH_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'PENDING_VALIDATION', label: 'Ready for review' },
  { value: 'VALIDATION_IN_PROGRESS', label: 'In review' },
  { value: 'ON_HOLD', label: 'On hold' },
  { value: 'VALIDATED', label: 'Approved' },
  { value: 'SUBMITTED', label: 'Delivered' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'ARCHIVED', label: 'Archived' },
];
